import { css } from '@emotion/core';

export const TOOLBAR_HEIGHT = 42;

/* Height is set inline from the computed book size, so the frame hugs the book.
   The min-height only covers the moment before the PDF's page ratio is known. */
export const shell = css`
  position: relative;
  background: #2a2a2a;
  display: flex;
  flex-direction: column;
  min-height: 260px;
  overflow: hidden;
  user-select: none;

  &:focus { outline: none; }
`;

export const shellFullscreen = css`
  background: #111;
  height: 100vh;
  min-height: 100vh;
`;

/* Stand-in for platforms with no element Fullscreen API (iPhone Safari).
   Applied after shellFullscreen, so it overrides the 100vh: on iOS that is the
   *large* viewport height and would run on behind the URL bar. Letting top and
   bottom drive the height keeps the shell inside the visible viewport. */
export const shellPseudoFullscreen = css`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 100000;
  height: auto;
  min-height: 0;
`;

/* No padding here on purpose - clientWidth/clientHeight are read directly as
   the space available to the book, and padding would inflate both. */
export const stage = css`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const book = css`
  position: relative;
  z-index: 1;

  /* The library ships no cursor of its own, so a draggable book reads as inert.
     grab/grabbing says "drag me" rather than merely "clickable", which matches
     what the corner drag actually does. Touch devices ignore all of this. */
  .stf__parent {
    box-shadow: 0 30px 60px rgba(0,0,0,0.55), 0 10px 20px rgba(0,0,0,0.35);
    cursor: grab;
  }
  .stf__parent:active {
    cursor: grabbing;
  }
  .stf__block {
    background: #fff;
  }
  /* Pages are rasterised at their display width, so no CSS scaling here -
     stretching the canvas is what made them blurry. */
  .react-pdf__Page,
  .react-pdf__Page__canvas {
    display: block;
    background: #fff;
  }
`;

export const page = css`
  background: #fff;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

`;

export const toolbar = css`
  flex: 0 0 auto;
  height: ${TOOLBAR_HEIGHT}px;
  background: rgba(0,0,0,0.85);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  color: #fff;
  font-size: 13px;
  z-index: 3;

  @media (min-width: 768px) {
    padding: 0 20px;
  }
`;

export const toolbarLeft = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const toolbarRight = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const btn = css`
  appearance: none;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 18px;
  line-height: 1;
  width: 34px;
  height: 34px;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background .15s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover { background: rgba(255,255,255,0.15); }
  &:disabled { opacity: 0.35; pointer-events: none; }
  &:focus { outline: none; }
  &:focus-visible { box-shadow: 0 0 0 2px rgba(255,255,255,0.35); }
`;

/* Colour is set explicitly - site-wide styles were bleeding into the counter. */
export const counter = css`
  color: #fff;
  font-size: 13px;
  min-width: 56px;
  text-align: center;
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
`;

export const jumpForm = css`
  display: none;

  @media (min-width: 600px) {
    display: block;
    margin-right: 4px;
  }
`;

export const jumpInput = css`
  width: 62px;
  height: 26px;
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 4px;
  background: rgba(255,255,255,0.08);
  color: #fff;
  font-size: 12px;
  text-align: center;
  padding: 0 6px;

  &::placeholder { color: rgba(255,255,255,0.45); }
  &:focus {
    outline: none;
    border-color: rgba(255,255,255,0.6);
    background: rgba(255,255,255,0.14);
  }
`;

export const loading = css`
  color: #ccc;
  font-size: 14px;
  padding: 40px 0;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
`;

export const spinner = css`
  width: 34px;
  height: 34px;
  border: 3px solid rgba(255, 255, 255, 0.18);
  border-top-color: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: flipbook-spin 0.8s linear infinite;

  @keyframes flipbook-spin {
    to { transform: rotate(360deg); }
  }

  /* Readers who ask for less motion get a static ring rather than none, so the
     loading state still reads as "busy". */
  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2.4s;
  }
`;
